import React, { useEffect, useRef, useState } from "react";
import firebase from "../Firebase.js";
import LoadingContent from "../LoadingContent.jsx";
import PropTypes from "prop-types";
import AccountSetup from "./AccountSetup.js";
import loadAccountData from "./loadAccountData.js";
import {
  hasRequiredProfileDetails,
  initializeUserProfile,
} from "./userProfiles.js";

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [authentication, setAuthentication] = useState(null);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [account, setAccount] = useState({
    status: "loading",
    profile: {},
    user: null,
  });
  const signupDetails = useRef(null);
  const signupRequest = useRef(null);
  const authRevision = useRef(0);
  const database = firebase.firestore();
  const currentUser = account.user;
  const userInfo = account.profile;

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      authRevision.current += 1;
      setAccount({ status: "loading", profile: {}, user: null });
      setAuthentication({ user, revision: authRevision.current });
    });
    return () => {
      authRevision.current += 1;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!authentication) {
      return;
    }
    let cancelled = false;
    const { user, revision } = authentication;
    const isCurrentSession = () =>
      !cancelled && revision === authRevision.current;

    async function initializeAccount() {
      if (!user) {
        signupDetails.current = null;
        setAccount({ status: "ready", profile: {}, user: null });
        return;
      }
      setAccount({ status: "loading", profile: {}, user: null });
      try {
        const draft = signupDetails.current;
        const details =
          draft && draft.email.toLowerCase() === user.email?.toLowerCase()
            ? draft
            : {};
        const profile = await initializeUserProfile(database, user, details);
        if (!isCurrentSession()) {
          return;
        }
        if (!hasRequiredProfileDetails(profile)) {
          setAccount({ status: "needs-details", profile, user: null });
          return;
        }
        const accountData = await loadAccountData(database, profile);
        if (isCurrentSession()) {
          signupDetails.current = null;
          setAccount({ status: "ready", profile: accountData, user });
        }
      } catch (error) {
        if (isCurrentSession()) {
          console.error("Account initialization failed:", error);
          setAccount({ status: "error", profile: {}, user: null, error });
        }
      }
    }

    initializeAccount();
    return () => {
      cancelled = true;
    };
  }, [authentication, database, initializationAttempt]);

  function setUserInfo(profile) {
    setAccount((previous) => ({ ...previous, profile }));
  }

  function handleUserInfoChange(
    update,
    infoChange,
    deepUpdate,
    updatingCredit,
    localOnly = false,
  ) {
    if (localOnly) {
      setUserInfo({ ...update });
      return;
    }
    // If the user's the whole userId needs to be udated.
    if (infoChange) {
      update.userId = currentUser.uid;
      setUserInfo({ ...update });

      const batch = database.batch();
      const usersDocRef = database
        .collection("Users")
        .doc(firebase.auth().currentUser.uid);

      batch.set(usersDocRef, { ...update }, { merge: true });

      batch.commit();
    }
    // Checks to see if the user needs to fully overwrite their userInfo
    else if (deepUpdate) {
      // makes sure the userId is correct
      update.userId = currentUser.uid;
      // Updated the userInfo
      setUserInfo({ ...update });
      // Sets the Users update
      const batch = database.batch();
      const usersDocRef = database
        .collection("Users")
        .doc(firebase.auth().currentUser.uid);
      // commits and updates the database.
      batch.update(usersDocRef, { ...update });

      batch.commit();
    }
    // If the user's credits are being changed.
    else if (updatingCredit) {
      // Check to see if their credits are less than 0.  If they are then we need
      // to set their credits to 0
      if (parseFloat(update.credits) < 0) {
        // Sets the Users update
        const batch = database.batch();
        const usersDocRef = database.collection("Users").doc(update.userId);
        // Reset the credits to 0
        update.credits = 0;
        // commits and updates the database.
        batch.update(usersDocRef, { ...update });
        batch.commit();
        // Update the user hook.
        setUserInfo({ ...update });
      } else {
        // Update the user hook.
        setUserInfo({ ...update });
      }
    } else {
      setUserInfo({ ...userInfo, votedIssues: update });
    }
  }

  function handleSignUp(newUserInfo, password) {
    if (signupRequest.current) {
      return signupRequest.current;
    }
    // Set the non-password draft before Auth can emit its automatic sign-in.
    const draft = { ...newUserInfo };
    signupDetails.current = draft;
    signupRequest.current = firebase
      .auth()
      .createUserWithEmailAndPassword(draft.email, password)
      .catch((error) => {
        if (signupDetails.current === draft) {
          signupDetails.current = null;
        }
        throw error;
      })
      .finally(() => {
        signupRequest.current = null;
      });
    return signupRequest.current;
  }

  function retryInitialization() {
    setInitializationAttempt((attempt) => attempt + 1);
  }

  function completeProfile(details) {
    signupDetails.current = { ...details, email: authentication.user.email };
    retryInitialization();
  }

  if (account.status === "loading") {
    return <LoadingContent />;
  }
  if (account.status === "needs-details" || account.status === "error") {
    return (
      <AccountSetup
        profile={account.profile}
        error={account.error}
        onComplete={completeProfile}
        onRetry={retryInitialization}
        onSignOut={() => firebase.auth().signOut()}
      />
    );
  }
  return (
    <AuthContext.Provider
      value={{ currentUser, userInfo, handleUserInfoChange, handleSignUp }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};
